import CurrentMapChangedEvent from "../event/CurrentMapChangedEvent";
import MapLocationAvailabilityEvent from "../event/MapLocationAvailabilityEvent";
import MapCharacterDefinition from "./MapCharacterDefinition";
import MapDefinition from "./MapDefinition";
import MapLocationDefinition from "./MapLocationDefinition";

export default class MapModel {
    constructor(dispatcher, hubSaveGameModel) {
        this._dispatcher = dispatcher;
        this._hubSaveGameModel = hubSaveGameModel;

        /** @type {MapDefinition} */
        this._currentMap = null;

        /** @type {Array<MapLocationDefinition>} */
        this._activeLocations = [];
        /** @type {Array<MapCharacterDefinition>} */
        this._activeCharacters = [];
    }

    get currentMapId() {
        return this._currentMap.id;
    }

    get locations() {
        return this._currentMap.locations;
    }

    get characters() {
        return this._currentMap.characters;
    }

    /**
     * @param {MapDefinition} map 
     */
    setCurrentMap(map) {
        this._currentMap = map;
        // Reset active locations and characters as we're changing the map.
        this._activeLocations = [];
        this._activeCharacters = [];

        const evt = new CurrentMapChangedEvent(CurrentMapChangedEvent.CURRENT_MAP_CHANGED, map);
        this._dispatcher.dispatch(evt.type, { data: evt });
    }

    resetHubSaveGame() {
        this._hubSaveGameModel._mapLocations = [];
    }

    /**
     * @param {MapCharacterDefinition} character 
     * @returns {MapLocationDefinition}
     */
    getLocationForCharacter(character) {
        return this.locations.find(l => l.id === character.location);
    }

    /**
     * @param {MapCharacterDefinition} character 
     */
    activateCharacter(character) {
        this._activeCharacters.push(character);
    }

    /**
     * @param {MapCharacterDefinition} character 
     */
    deactivateCharacter(character) {
        const index = this._activeCharacters.indexOf(character);
        this._activeCharacters.splice(index, 1);
    }

    /**
     * @param {MapCharacterDefinition} character 
     * @returns {boolean}
     */
    characterIsActive(character) {
        return this._activeCharacters.indexOf(character) > -1;
    }

    /**
     * @param {MapLocationDefinition} location 
     */
    activateLocation(location) {
        this._activeLocations.push(location);
        this._hubSaveGameModel.enableMapLocation(location.id, true);
        const mapLocationAvailabilityEvent = new MapLocationAvailabilityEvent(MapLocationAvailabilityEvent.LOCATION_AVAILABLE, location);
        this._dispatcher.dispatch(mapLocationAvailabilityEvent.type, { data: mapLocationAvailabilityEvent });
    }

    /**
     * @param {MapLocationDefinition} location 
     */
    deactivateLocation(location) {
        const index = this._activeLocations.indexOf(location);
        this._activeLocations.splice(index, 1);
        this._hubSaveGameModel.enableMapLocation(location.id, false);
        const mapLocationAvailabilityEvent = new MapLocationAvailabilityEvent(MapLocationAvailabilityEvent.LOCATION_UNAVAILABLE, location);
        this._dispatcher.dispatch(mapLocationAvailabilityEvent.type, { data: mapLocationAvailabilityEvent });
    }

    /**
     * @param {MapLocationDefinition} location 
     * @returns {boolean}
     */
    locationIsActive(location) {
        return this._activeLocations.indexOf(location) > -1;
    }

    /**
     * @param {MapLocationDefinition} location 
     * @returns {Array<MapCharacterDefinition>}
     */
    getActiveCharactersForLocation(location) {
        return this.characters.filter(c => this.characterIsActive(c) && c.location === location.id);
    }

    makeCharacterAvailable(characterId) {
        const character = this.characters.find(c => c.id === characterId);
        if(character != null) {
            const locationForCharacter = this.getLocationForCharacter(character);
            this.activateCharacter(character);
            if (!this.locationIsActive(locationForCharacter)) {
                this.activateLocation(locationForCharacter);
            }
        }
    }

    makeCharacterUnavailable(characterId) {
        const character = this.characters.find(c => c.id === characterId);
        if(character != null) {
            const locationForCharacter = this.getLocationForCharacter(character);
            this.deactivateCharacter(character);

            if (this.getActiveCharactersForLocation(locationForCharacter).length === 0) {
                this.deactivateLocation(locationForCharacter);
            }
        }
    }
}